/**
 * SSOT display resolver for hotel agent handling fee on dashboard order detail.
 * Prevents stale snapshot / orders.handlingfee from overwriting remediated values.
 */

const REMEDIATION_SOURCE_MARKERS = [
  "legacy_raw_usd_addend_remediation",
  "legacy_raw_usd_addend",
  "known_remediation_order_",
  "legacy_usd_wire_remediation",
  "usd_wire_display_remediation",
  "handling_fee_display_ssot",
];

function formatUsdLabel(n) {
  return `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

function formatIlsLabel(n) {
  return `₪${Number(n).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export function formatHotelHandlingFeeDisplayText(display) {
  if (!display) return null;
  const { amountUsd, amountIls } = display;
  if (amountUsd != null && amountIls != null) {
    return `${formatUsdLabel(amountUsd)} / ${formatIlsLabel(amountIls)}`;
  }
  if (amountIls != null) return formatIlsLabel(amountIls);
  if (amountUsd != null) return formatUsdLabel(amountUsd);
  return null;
}

function isRemediatedSource(source) {
  if (!source) return false;
  return REMEDIATION_SOURCE_MARKERS.some((m) => source.includes(m));
}

function parseNum(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
}

function isLegacyRawUsdAddendBlock(block) {
  if (!block || typeof block !== "object") return false;
  if (block.legacy_raw_usd_addend || block.usd_wire_display_remediation) return true;
  const src = block.source_path || block.source || block.source_field || "";
  return typeof src === "string" && REMEDIATION_SOURCE_MARKERS.some((m) => src.includes(m));
}

function resolveLegacyRawUsdAddendDisplay(orderRow, adminPb, pc) {
  const agentHf =
    adminPb?.agent_handling_fee && typeof adminPb.agent_handling_fee === "object"
      ? adminPb.agent_handling_fee
      : null;
  const displaySsot =
    pc?.handling_fee_display_ssot && typeof pc.handling_fee_display_ssot === "object"
      ? pc.handling_fee_display_ssot
      : null;

  const isLegacy =
    isLegacyRawUsdAddendBlock(agentHf) || isLegacyRawUsdAddendBlock(displaySsot);
  if (!isLegacy) return null;

  const amountUsd =
    parseNum(displaySsot?.amount_usd) ??
    parseNum(agentHf?.amount_usd) ??
    parseNum(orderRow?.handlingfee);
  if (amountUsd == null) return null;

  const rate =
    parseNum(adminPb?.exchange_rate_at_booking) ??
    parseNum(pc?.saved_rate) ??
    parseNum(orderRow?.exchange_rate_at_booking);

  const amountIls =
    parseNum(displaySsot?.amount_ils) ??
    parseNum(agentHf?.amount_ils) ??
    (rate && rate > 0 ? Math.round(amountUsd * rate * 100) / 100 : null);

  const sourcePath =
    (typeof displaySsot?.source_path === "string" && displaySsot.source_path) ||
    (typeof agentHf?.source === "string" && agentHf.source) ||
    (typeof agentHf?.source_field === "string" && agentHf.source_field) ||
    "legacy_raw_usd_addend_remediation";

  return {
    amountUsd,
    amountIls,
    sourcePath,
    locked: true,
  };
}

function fromSsotBlock(block) {
  if (!block || typeof block !== "object") return null;
  const amountUsd = parseNum(block.amount_usd);
  const amountIls = parseNum(block.amount_ils);
  const sourcePath =
    (typeof block.source_path === "string" && block.source_path) ||
    (typeof block.source === "string" && block.source) ||
    (typeof block.source_field === "string" && block.source_field) ||
    null;
  if (amountUsd === null && amountIls === null) return null;
  return {
    amountUsd,
    amountIls,
    sourcePath,
    locked: Boolean(block.locked) || isRemediatedSource(sourcePath),
  };
}

export function isAuthoritativeHotelHandlingFeeSource(sourcePath, locked) {
  if (locked) return true;
  if (!sourcePath) return false;
  return REMEDIATION_SOURCE_MARKERS.some((m) => sourcePath.includes(m));
}

export function isLockedHotelHandlingFeeDisplay(display) {
  return Boolean(display?.locked);
}

/**
 * Resolve handling fee for display from price-calculations payload (+ optional order row).
 * Never downgrade a locked/remediated SSOT result using raw orders.handlingfee.
 */
export function resolveHotelAgentHandlingFeeDisplay(
  priceCalculations,
  orderRow,
  existing
) {
  const pc =
    priceCalculations && typeof priceCalculations === "object" ? priceCalculations : {};
  const adminPb =
    pc.admin_price_breakdown && typeof pc.admin_price_breakdown === "object"
      ? pc.admin_price_breakdown
      : null;

  const legacyRawDisplay = resolveLegacyRawUsdAddendDisplay(orderRow, adminPb, pc);
  if (legacyRawDisplay) {
    return legacyRawDisplay;
  }

  const agentHf =
    adminPb?.agent_handling_fee && typeof adminPb.agent_handling_fee === "object"
      ? adminPb.agent_handling_fee
      : null;

  const candidates = [];

  const displaySsot = fromSsotBlock(pc.handling_fee_display_ssot);
  if (displaySsot) candidates.push(displaySsot);

  const fromAdmin = fromSsotBlock(agentHf);
  if (fromAdmin) candidates.push(fromAdmin);

  const now = pc.now && typeof pc.now === "object" ? pc.now : null;
  const nowIls = parseNum(now?.handling_fee_ils);
  const nowUsd = parseNum(now?.fee_value_usd);
  if (nowIls !== null || nowUsd !== null) {
    candidates.push({
      amountUsd: nowUsd,
      amountIls: nowIls,
      sourcePath: "price-calculations.now.handling_fee_ils",
      locked: false,
    });
  }

  const feeValue = parseNum(now?.fee_value);
  const feeValueUsd = parseNum(now?.fee_value_usd);
  if (feeValue !== null && feeValueUsd === null && nowIls === null) {
    candidates.push({
      amountUsd: null,
      amountIls: feeValue,
      sourcePath: "price-calculations.now.fee_value",
      locked: false,
    });
  }

  let best = candidates.find((c) => c.locked) ?? candidates[0] ?? null;

  if (existing?.locked && best && !best.locked) {
    best = existing;
  }

  if (!best) {
    const hf = parseNum(orderRow?.handlingfee);
    const rate = parseNum(pc.saved_rate) ?? parseNum(orderRow?.rates?.USD);
    if (hf !== null && rate && rate > 0) {
      return {
        amountIls: hf,
        amountUsd: Math.round((hf / rate) * 100) / 100,
        sourcePath: "orders.handlingfee / exchange_rate_at_booking",
        locked: false,
      };
    }
    return { amountUsd: null, amountIls: null, sourcePath: null, locked: false };
  }

  if (best.amountUsd === null && best.amountIls !== null) {
    const rate = parseNum(pc.saved_rate);
    if (rate && rate > 0) {
      best = { ...best, amountUsd: Math.round((best.amountIls / rate) * 100) / 100 };
    }
  }
  if (best.amountIls === null && best.amountUsd !== null) {
    const rate = parseNum(pc.saved_rate);
    if (rate && rate > 0) {
      best = { ...best, amountIls: Math.round(best.amountUsd * rate * 100) / 100 };
    }
  }

  return best;
}

export function traceHotelHandlingFeeDisplay(componentName, endpoint, display) {
  const dev =
    (typeof import.meta !== "undefined" && import.meta.env?.DEV) ||
    (typeof process !== "undefined" && process.env?.NODE_ENV === "development");
  if (!dev) return;
  console.info("[HOTEL_HANDLING_FEE_DISPLAY_TRACE]", {
    ...display,
    componentName,
    endpoint,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Merge a new price-calculations fetch into component state without clobbering locked SSOT.
 */
export function mergeHotelPriceCalculationsState(prev, next) {
  if (!next) return prev ?? null;
  if (!prev) return next;

  const prevDisplay = resolveHotelAgentHandlingFeeDisplay(prev);
  const nextDisplay = resolveHotelAgentHandlingFeeDisplay(next);

  if (prevDisplay.locked && !nextDisplay.locked) {
    const prevAdmin =
      typeof prev.admin_price_breakdown === "object" ? prev.admin_price_breakdown : {};
    const nextAdmin =
      typeof next.admin_price_breakdown === "object" ? next.admin_price_breakdown : {};
    const prevAgent =
      typeof prevAdmin.agent_handling_fee === "object" ? prevAdmin.agent_handling_fee : {};
    const nextAgent =
      typeof nextAdmin.agent_handling_fee === "object" ? nextAdmin.agent_handling_fee : {};
    const prevNow = typeof prev.now === "object" ? prev.now : {};
    const nextNow = typeof next.now === "object" ? next.now : {};

    return {
      ...next,
      handling_fee_display_ssot: prev.handling_fee_display_ssot ?? next.handling_fee_display_ssot,
      admin_price_breakdown: {
        ...nextAdmin,
        agent_handling_fee: { ...nextAgent, ...prevAgent },
      },
      now: {
        ...nextNow,
        fee_value: prevDisplay.amountIls ?? nextNow.fee_value,
        fee_value_usd: prevDisplay.amountUsd ?? nextNow.fee_value_usd,
        handling_fee_ils: prevDisplay.amountIls ?? nextNow.handling_fee_ils,
      },
    };
  }

  return next;
}
